import type {FabricObject, Point} from '@postermywall/fabricjs-2';
import {Canvas} from '@postermywall/fabricjs-2';

type TargetSearchResult = ReturnType<Canvas['searchPossibleTargets']>;

/**
 * The editor page's canvas: fabric's per-pixel target finding plus a bounding-box fallback.
 *
 * Per-pixel finding keeps items UNDER a transparent region selectable (the reason it is on). But
 * when that pass hits nothing opaque — only the page background, or nothing at all — the topmost
 * item whose bounding box contains the pointer becomes the target instead, so a textbox is
 * hoverable and clickable across its padding, empty list rows and the gaps between glyphs.
 */
export class PosterCanvas extends Canvas {
  /**
   * Identifies the page-background item's fabric object, injected by the page. The background
   * catches every per-pixel miss (it spans the page and opts out of per-pixel finding), so the
   * fallback treats hitting it the same as hitting nothing.
   */
  public isPageBackgroundObject: (fabricObject: FabricObject) => boolean = (): boolean => false;

  /** Off in webpage/embed rendering, where bounding-box targeting would change link hit areas. */
  public isBoundingBoxTargetFallbackEnabled = false;

  private isInBoundingBoxOnlyPass = false;

  override searchPossibleTargets(objects: FabricObject[], pointer: Point): TargetSearchResult {
    const perPixelResult = super.searchPossibleTargets(objects, pointer);
    if (!this.isBoundingBoxTargetFallbackEnabled || this.isInBoundingBoxOnlyPass) {
      return perPixelResult;
    }
    if (perPixelResult.target && !this.isPageBackgroundObject(perPixelResult.target)) {
      return perPixelResult;
    }
    const boundingBoxResult = this.#searchBoundingBoxOnlyTargets(objects, pointer);
    if (boundingBoxResult.target && !this.isPageBackgroundObject(boundingBoxResult.target)) {
      return boundingBoxResult;
    }
    return perPixelResult;
  }

  /**
   * Treats every pixel as opaque while the bounding-box pass runs, so `_checkTarget` reduces to
   * the plain containment check.
   */
  override isTargetTransparent(target: FabricObject, x: number, y: number): boolean {
    if (this.isInBoundingBoxOnlyPass) {
      return false;
    }
    return super.isTargetTransparent(target, x, y);
  }

  #searchBoundingBoxOnlyTargets(objects: FabricObject[], pointer: Point): TargetSearchResult {
    this.isInBoundingBoxOnlyPass = true;
    try {
      return super.searchPossibleTargets(objects, pointer);
    } finally {
      this.isInBoundingBoxOnlyPass = false;
    }
  }
}
